import React, { useRef, useEffect } from 'react';

const PARTICLE_COUNT = 28;
const CONNECT_DISTANCE = 130;
const COLLISION_DISTANCE = 26;
const EXPLOSION_COOLDOWN = 90;
const PARTICLE_RADIUS = 5;
const STAR_RATIO = 0.4; // 40% serán estrellas (pequeñas y brillantes)
const STAR_RADIUS_MIN = 1;
const STAR_RADIUS_MAX = 2.5;
const STAR_ALPHA_MIN = 0.65;
const STAR_ALPHA_MAX = 0.95;

const ParticlesBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
      initParticles(rect.width, rect.height);
    };

    const initParticles = (w, h) => {
      particles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const isStar = Math.random() < STAR_RATIO;
        const radius = isStar
          ? STAR_RADIUS_MIN + Math.random() * (STAR_RADIUS_MAX - STAR_RADIUS_MIN)
          : PARTICLE_RADIUS;
        const baseAlpha = isStar
          ? STAR_ALPHA_MIN + Math.random() * (STAR_ALPHA_MAX - STAR_ALPHA_MIN)
          : 0.2;
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.25,
          explosionCooldown: 0,
          radius,
          baseAlpha,
        });
      }
    };

    const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

    const animate = () => {
      const w = canvas.getBoundingClientRect().width;
      const h = canvas.getBoundingClientRect().height;

      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (p.explosionCooldown > 0) p.explosionCooldown--;

        p.x += p.vx;
        p.y += p.vy;

        const r = p.radius ?? PARTICLE_RADIUS;
        if (p.x < r || p.x > w - r) p.vx *= -1;
        if (p.y < r || p.y > h - r) p.vy *= -1;
        p.x = Math.max(r, Math.min(w - r, p.x));
        p.y = Math.max(r, Math.min(h - r, p.y));
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const d = distance(p1, p2);

          if (d < COLLISION_DISTANCE && p1.explosionCooldown === 0 && p2.explosionCooldown === 0) {
            const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
            const force = 0.8;
            p1.vx -= Math.cos(angle) * force;
            p1.vy -= Math.sin(angle) * force;
            p2.vx += Math.cos(angle) * force;
            p2.vy += Math.sin(angle) * force;
            p1.explosionCooldown = EXPLOSION_COOLDOWN;
            p2.explosionCooldown = EXPLOSION_COOLDOWN;
          }
        }
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const d = distance(p1, p2);

          if (d < CONNECT_DISTANCE && p1.explosionCooldown < EXPLOSION_COOLDOWN * 0.6 && p2.explosionCooldown < EXPLOSION_COOLDOWN * 0.6) {
            const opacity = 0.06 + 0.14 * (1 - d / CONNECT_DISTANCE);
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      for (const p of particles) {
        const baseAlpha = p.baseAlpha ?? 0.2;
        const radius = p.radius ?? PARTICLE_RADIUS;
        const alpha = p.explosionCooldown > 0
          ? baseAlpha * (0.5 + 0.5 * (1 - p.explosionCooldown / EXPLOSION_COOLDOWN))
          : baseAlpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.5})`;
        ctx.lineWidth = radius > 3 ? 1 : 0.5;
        ctx.stroke();
      }

      animationId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} style={{ display: 'block' }} />;
};

export default ParticlesBackground;
